import { useEffect, useState, useCallback } from "react";
import {
  getFirebaseToken,
  initFirebaseTokenListener,
  getLastRegisteredToken,
  saveLastRegisteredToken,
  type FirebaseTokenInfo,
} from "@/utils/firebase-token";
import { initNativePackageListener } from "@/utils/app-package";
import { useRegisterDeviceToken, useUnregisterDeviceToken } from "@/api/services/main/app-notification";
import Cookies from "js-cookie";

export const FCM_USER_LOGIN_EVENT_NAME = "userLoggedIn";

/**
 * Initializes all native bridge listeners (safe area, firebase token, package metadata).
 * Firebase token is fetched and cached on init, but only registered when authenticated.
 * Listens for FCM_USER_LOGIN_EVENT_NAME event to register token after login.
 * Call once at app root level.
 * @returns ready — true when safe area insets are received (or timeout).
 */
export function useNativeBridge() {
  const [ready, setReady] = useState(false);
  const { mutateAsync: registerTokenAsync } = useRegisterDeviceToken();
  const { mutateAsync: unregisterTokenAsync } = useUnregisterDeviceToken();

  const handleTokenChange = useCallback(
    async (newInfo: FirebaseTokenInfo) => {
      if (!isAuthenticated()) return;

      const lastRegistered = getLastRegisteredToken();

      // If token is same as last registered, do nothing
      if (lastRegistered?.fcmToken === newInfo.fcmToken) {
        return;
      }

      try {
        // 1. Unregister old token if it exists and is different
        if (lastRegistered?.fcmToken) {
          await unregisterTokenAsync({ deviceToken: lastRegistered.fcmToken }).catch((err) => {
            console.error("Failed to unregister old token:", err);
          });
        }

        // 2. Register new token
        await registerTokenAsync(buildRegisterPayload(newInfo));

        // 3. Save to localStorage on success
        saveLastRegisteredToken(newInfo);
      } catch (error) {
        console.error("Failed to register new token:", error);
      }
    },
    [registerTokenAsync, unregisterTokenAsync],
  );

  useEffect(() => {
    // All native bridge logic only runs inside native webview
    if (!isNativeWebView()) {
      setReady(true);
      return;
    }

    // Fetch initial firebase token; only register if already logged in
    getFirebaseToken().then((info) => {
      if (info?.fcmToken) {
        handleTokenChange(info);
      }
    });

    // After login: fetch current token from Native and register it
    const handleLoggedIn = async () => {
      const currentToken = await getFirebaseToken();
      if (currentToken?.fcmToken) {
        handleTokenChange(currentToken);
      }
    };
    window.addEventListener(FCM_USER_LOGIN_EVENT_NAME, handleLoggedIn);

    // On token change: unregister old token first, then register new one (only if authenticated)
    const cleanupFirebase = initFirebaseTokenListener((newInfo) => {
      handleTokenChange(newInfo);
    });

    const cleanupNativePackage = initNativePackageListener();

    setReady(true);

    return () => {
      window.removeEventListener(FCM_USER_LOGIN_EVENT_NAME, handleLoggedIn);
      cleanupFirebase();
      cleanupNativePackage();
    };
  }, [handleTokenChange]);

  return { ready };
}

function isAuthenticated(): boolean {
  return !!Cookies.get("learner-token");
}

function buildRegisterPayload(info: FirebaseTokenInfo) {
  return {
    deviceToken: info.fcmToken,
    platform: info.platform as "ios" | "android",
    deviceId: info.deviceId || undefined,
    appVersion: info.appVersion || undefined,
  };
}

export function isNativeWebView(): boolean {
  return !!window.nativeDevice;
}
